<template>
  <div class="modal-overlay planning">
    <div class="modal-content" :class="{ 'modal-content--with-map': showMapPanel }">
      <div class="modal-header">
        <h3><span v-if="searchLoading">Recherche en cours...</span><span v-else>Modifier le créneau</span></h3>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <div class="modal-layout">
      <div class="modal-body">
        <section class="meeting-summary">
          <div class="meeting-summary-head">
            <p class="meeting-summary-kicker">Créneau</p>
            <strong class="meeting-summary-date">{{ meetingDateLabel }}</strong>
            <p class="meeting-summary-time">{{ meetingTimeLabel }}</p>
          </div>

          <div v-if="formData.hasMeetLink" class="meeting-summary-address-wrap">
            <div class="slot-meet-link">
              <strong class="slot-meet-link-label">Lien de la visio : </strong>
              <a
                  v-if="normalizedMeetLink && normalizedMeetLink.startsWith('http')"
                  :href="normalizedMeetLink"
                  class="slot-meet-link-anchor"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <i class="bi bi-camera-video-fill slot-meet-link-icon" aria-hidden="true"></i>
                <span>{{ normalizedMeetLink }}</span>
              </a>
              <span v-else-if="normalizedMeetLink" class="slot-meet-link-anchor">
                <i class="bi bi-camera-video-fill slot-meet-link-icon" aria-hidden="true"></i>
                <span>{{ normalizedMeetLink }}</span>
              </span>
              <span v-else class="slot-meet-link-anchor slot-meet-link-anchor--placeholder">
                <i class="bi bi-camera-video-fill slot-meet-link-icon" aria-hidden="true"></i>
                <span>Lien à renseigner</span>
              </span>
            </div>
          </div>

          <div v-else-if="meetingAddress" class="meeting-summary-address-wrap">
            <strong class="meeting-summary-address-title">{{ meetingAddress.ofName }}</strong>
            <address class="slot-address">
              <div class="slot-address-line">
                <span class="slot-address-pin">📍</span>
                <div>
                  {{ meetingAddress.campusAddress }}<br>
                  <span class="slot-address-city">
                    {{ meetingAddress.campusPostalCode }} {{ meetingAddress.campusCityName }}
                  </span>
                </div>
              </div>
            </address>
          </div>
        </section>

        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>
        <form @submit.prevent="submitForm">
          <div v-if="!(meeting.candidacyObjId && formData.candidacyObjId)" class="form-group">
            <label class="form-label" for="meetingVisibility">Visibilité du créneau :</label>
            <label class="visibility-switch">
              <input
                  id="meetingVisibility"
                  v-model="formData.public"
                  :disabled="!isMeetingVisibilityEditable || loading"
                  type="checkbox"
              />
              <span class="visibility-slider"></span>
              <span class="visibility-label">{{ formData.public ? 'Publique' : 'Privée' }}</span>
            </label>
            <small v-if="isMeetingVisibilityEditable" class="form-text text-muted">
              Les créneaux publics sont visibles côté candidat.
            </small>
          </div>

          <!-- Recherche de candidature -->
          <div class="form-group">
            <SearchInput
                v-model="searchTerm"
                :clear-button-title="UI_MESSAGES.CLEAR_BUTTON_TITLE"
                :has-value="!!formData.candidacyObjId"
                :loading="searchLoading"
                :help-text="UI_MESSAGES.HELP_TEXT_LEAVE_EMPTY"
                :label="'Candidature :'"
                :placeholder="formData.candidacyObjId ? UI_MESSAGES.SEARCH_CANDIDACY_PLACEHOLDER_MODIFY : UI_MESSAGES.SEARCH_CANDIDACY_PLACEHOLDER"
                :suggestion-key-field="'candidacyObjId'"
                :suggestions="suggestions"
                @clear="clearSelection"
                @search="onSearch"
                @select="selectCandidacy"
                @clear-suggestions="clearSuggestions"
            >
              <template #suggestion="{ suggestion }">
                {{ formatPersonDisplayName(suggestion) }}
                <small v-if="suggestion.email"> — {{ suggestion.email }}</small>
              </template>
            </SearchInput>

            <div v-if="quickCandidacy && !formData.candidacyObjId" class="quick-candidacy-wrap">
              <button
                  :class="{ 'is-selected': formData.candidacyObjId === quickCandidacy.candidacyObjId }"
                  class="quick-candidacy-pill"
                  type="button"
                  @click="selectQuickCandidacy"
              >
                <span class="quick-candidacy-pill-name">{{ formatCandidacyName(quickCandidacy) }}</span>
                <small v-if="quickCandidacy.email" class="quick-candidacy-pill-email">{{ quickCandidacy.email }}</small>
              </button>
            </div>
            <div v-if="candidacyDistance !== null">
             <span  class="candidacy-distance">
                📍 {{ selectedCandidacy?.codePostal }} {{ selectedCandidacy?.ville }}, distance &gt; {{ candidacyDistance }} kms du centre
            </span>
            </div>
          </div>




          <!-- Statut du meeting -->
          <div v-if="meeting.candidacyObjId && formData.candidacyObjId" class="form-group">
            <label class="form-label">Modifier le statut de l'entretien :</label>
            <div class="status-picker">
              <div class="status-picker-btns" role="group" aria-label="Choisir le statut">
                <button
                    type="button"
                    :class="['status-picker-btn', 'status-picker-btn--accepted', { 'is-active': formData.meetingStatut === PLANNING_STATUS.ACCEPTED }]"
                    @click="setMeetingStatut(PLANNING_STATUS.ACCEPTED)"
                >
                  <span class="status-radio" aria-hidden="true"></span>
                  Accepté
                </button>
                <button
                    type="button"
                    :class="['status-picker-btn', 'status-picker-btn--refused', { 'is-active': formData.meetingStatut === PLANNING_STATUS.REFUSED }]"
                    @click="setMeetingStatut(PLANNING_STATUS.REFUSED)"
                >
                  <span class="status-radio" aria-hidden="true"></span>
                  Refusé
                </button>
                <button
                    type="button"
                    :class="['status-picker-btn', 'status-picker-btn--cancelled', { 'is-active': formData.meetingStatut === PLANNING_STATUS.CANCELLED }]"
                    @click="setMeetingStatut(PLANNING_STATUS.CANCELLED)"
                >
                  <span class="status-radio" aria-hidden="true"></span>
                  Annulé
                </button>
              </div>
            </div>
          </div>

          <div v-if="hasCandidacy || isMeetLinkValid" class="form-group">
            <div class="form-label-row">
              <label class="form-label" for="meetingHasMeetLink">Lien visio :</label>
            </div>
            <label class="visibility-switch">
              <input
                  id="meetingHasMeetLink"
                  v-model="formData.hasMeetLink"
                  :disabled="loading"
                  type="checkbox"
              />
              <span class="visibility-slider"></span>
              <span class="visibility-label">{{ formData.hasMeetLink ? 'Activé' : 'Désactivé' }}</span>
            </label>
            <input
                id="meetingMeetLink"
                v-model.trim="formData.meetLink"
                :class="{ 'is-invalid': formData.hasMeetLink && !isMeetLinkValid }"
                :disabled="!formData.hasMeetLink || loading"
                :required="formData.hasMeetLink"
                class="form-control mt-2"
                inputmode="url"
                placeholder="https://meet.google.com/..."
                type="text"
            />
            <small v-if="formData.hasMeetLink && !isMeetLinkValid" class="form-text text-danger">
              Le lien visio doit être une URL HTTPS valide
            </small>
          </div>

          <div class="form-group">
            <label for="meetingComments">Note interne :</label>
            <textarea
                id="meetingComments"
                v-model="formData.comments"
                class="form-control"
                placeholder="Saisir une note..."
                rows="5"
            />
            <small class="form-text text-muted">Laissez vide pour supprimer la note.</small>
          </div>


          <div class="form-actions">
            <button
                v-if="formData.meetingStatut !== PLANNING_STATUS.CANCELLED"
                class="btn btn-danger"
                type="button"
                @click="cancelMeeting"
            ><span style="right: 5px;">🗑️</span>
              Supprimer
            </button>
            <button
                :disabled="loading"
                :class="['btn', hasChanges ? 'btn-primary' : 'btn-secondary']"
                type="button"
                @click="hasChanges ? submitForm() : close()"
            >
              <i v-if="!loading && hasChanges" class="bi bi-pencil-fill" aria-hidden="true" style="margin-right: 5px;"></i>
              {{ loading ? 'Mise à jour...' : hasChanges ? 'Mettre à jour' : 'Fermer' }}
            </button>
          </div>
        </form>
      </div>

      <div v-if="showMapPanel" class="modal-map-pane">
        <iframe
          :src="mapsEmbedUrl"
          class="modal-map-iframe"
          frameborder="0"
          style="border:0"
          allowfullscreen
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, reactive, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {usePlanningStore} from '@/store/planningStore'
import {usePlanningStatus} from '@/composables/usePlanningStatus'
import {useDateFormatter} from '@/composables/useDateFormatter'
import {formatPersonDisplayName, useSearchAutocomplete} from '@/composables/useSearchAutocomplete'
import SearchInput from '@/components/UI/SearchInput.vue'
import {PLANNING_STATUS, SEARCH_CONFIG, UI_MESSAGES} from '@/constants/planning'
import {normalizeMapsAddress} from '@/utils/normalizeMapsAddress'

const emit = defineEmits(['close', 'updated'])

const props = defineProps({
  meeting: {
    type: Object,
    required: true
  },
  planning: {
    type: Object,
    required: true
  }
})

const route = useRoute()
const planningStore = usePlanningStore()
const {getStatusText, getStatusClass} = usePlanningStatus()
const {formatDate, formatTime} = useDateFormatter()
const loading = ref(false)
const error = ref(null)
const originalMeetingStatut = ref(null)
const quickCandidacy = ref(null)
const selectedCandidacy = ref(null)
const quickCandidacyLoading = ref(false)
let quickLookupRequestId = 0
const BOOTSTRAP_ICONS_HREF = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css'

const formData = reactive({
  candidacyObjId: props.meeting.candidacyObjId,
  meetingStatut: Number(props.meeting.statut),
  comments: typeof props.meeting.comments === 'string' ? props.meeting.comments : '',
  public: Boolean(props.meeting.public),
  hasMeetLink: props.meeting.meetLink !== null && props.meeting.meetLink !== undefined,
  meetLink: typeof props.meeting.meetLink === 'string' ? props.meeting.meetLink : ''
})

const safeDateLabel = (dateValue) => {
  if (!dateValue) return 'Date inconnue'
  return formatDate(dateValue)
}

const safeTimeLabel = (dateValue) => {
  if (!dateValue) return '--:--'
  return formatTime(dateValue)
}

const meetingDateLabel = computed(() => safeDateLabel(props.meeting.startAt))
const meetingTimeLabel = computed(() => `${safeTimeLabel(props.meeting.startAt)} à ${safeTimeLabel(props.meeting.endAt)}`)

const meetingAddress = computed(() => {
  return props.planning?.ofCandidatureMeeting || props.meeting?.planning?.ofCandidatureMeeting || null
})

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371
  const toRad = (deg) => deg * Math.PI / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const candidacyDistance = computed(() => {
  const address = meetingAddress.value
  const candidacy = selectedCandidacy.value
  if (!address?.campusLatitude || !address?.campusLongitude) return null
  if (!candidacy?.latitude || !candidacy?.longitude) return null
  return Math.ceil(haversineKm(address.campusLatitude, address.campusLongitude, candidacy.latitude, candidacy.longitude) / 10) * 10
})

const showMapPanel = computed(() => candidacyDistance.value !== null)

const mapsEmbedUrl = computed(() => {
  const address = meetingAddress.value
  const candidacy = selectedCandidacy.value
  if (!address?.campusAddress || !address?.campusPostalCode || !address?.campusCityName) return ''
  if (!candidacy?.latitude || !candidacy?.longitude) return ''
  const dest = encodeURIComponent(normalizeMapsAddress(`${address.campusAddress}, ${address.campusPostalCode} ${address.campusCityName}`))
  return `https://maps.google.com/maps?saddr=${candidacy.latitude},${candidacy.longitude}&daddr=${dest}&z=8&output=embed`
})

const hasChanges = computed(() => {
  const m = props.meeting
  if (formData.candidacyObjId !== m.candidacyObjId) return true
  if (formData.meetingStatut !== Number(m.statut)) return true
  if (formData.comments !== (typeof m.comments === 'string' ? m.comments : '')) return true
  if (formData.public !== Boolean(m.public)) return true
  const origHasMeetLink = m.meetLink !== null && m.meetLink !== undefined
  if (formData.hasMeetLink !== origHasMeetLink) return true
  if (formData.hasMeetLink && normalizedMeetLink.value !== (typeof m.meetLink === 'string' ? m.meetLink : '')) return true
  return false
})

const isMeetingVisibilityEditable = computed(() => !formData.candidacyObjId)
const hasCandidacy = computed(() => Boolean(formData.candidacyObjId))
const normalizedMeetLink = computed(() => {
  return typeof formData.meetLink === 'string' ? formData.meetLink.trim() : ''
})
const isValidIpv4 = (hostname) => {
  const ipv4Pattern = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/
  return ipv4Pattern.test(hostname)
}

const isValidDomainHostname = (hostname) => {
  const labels = hostname.split('.')
  if (labels.length < 2) {
    return false
  }

  const hasValidLabels = labels.every((label) => {
    return label.length > 0
      && label.length <= 63
      && /^[a-z0-9-]+$/i.test(label)
      && !label.startsWith('-')
      && !label.endsWith('-')
  })

  if (!hasValidLabels) {
    return false
  }

  const topLevelDomain = labels[labels.length - 1]
  return topLevelDomain.length >= 2 && /[a-z]/i.test(topLevelDomain)
}

const isValidHttpsUrl = (value) => {
  if (!value || /\s/.test(value)) {
    return false
  }

  try {
    const parsedUrl = new URL(value)
    if (parsedUrl.protocol !== 'https:' || !parsedUrl.hostname) {
      return false
    }

    const hostname = parsedUrl.hostname.toLowerCase()
    if (isValidIpv4(hostname)) {
      return true
    }

    return isValidDomainHostname(hostname)
  } catch {
    return false
  }
}

const isMeetLinkValid = computed(() => {
  return !formData.hasMeetLink || isValidHttpsUrl(normalizedMeetLink.value) || 'Lien visio à définir'
})

const ensureBootstrapIconsLoaded = () => {
  if (typeof document === 'undefined') {
    return
  }

  const existingLink = document.querySelector(`link[href="${BOOTSTRAP_ICONS_HREF}"]`)
  if (existingLink) {
    return
  }

  const link = document.createElement('link')
  link.href = BOOTSTRAP_ICONS_HREF
  link.rel = 'stylesheet'
  document.head.appendChild(link)
}

onMounted(() => {
  ensureBootstrapIconsLoaded()
})

// Recherche avec autocomplete - inchangée
const searchFunction = async (term) => {
  const promises = []


  if (props.planning.ofsIdsEffective && props.planning.ofsIdsEffective.length !== 0) {
    props.planning.ofsIdsEffective.forEach(id => {
      promises.push(
          planningStore.searchCandidacies(
              id,
              route.params.userAccountObjId,
              term
          )
      )
    })
  } else {
    promises.push(
        planningStore.searchCandidacies(
            route.params.ofObjId,
            route.params.userAccountObjId,
            term
        )
    )
  }

  const results = await Promise.all(promises)


  const allCandidacies = results.map(res => {
    if (Array.isArray(res)) {
      return res
    }
    return []
  }).flat()

  const uniqueCandidacy = []
  const seenIds = new Set()

  for (const candidacy of allCandidacies) {
    const id = candidacy.candidacyObjId
    if (id && !seenIds.has(id)) {
      seenIds.add(id)
      uniqueCandidacy.push(candidacy)
    }
  }

  return uniqueCandidacy
}

const formatCandidacyName = (candidacy) => {
  const fullName = formatPersonDisplayName(candidacy)
  if (fullName) {
    return fullName
  }

  return candidacy?.candidacyObjId
      ? `Candidature ${candidacy.candidacyObjId}`
      : 'Candidature'
}

const loadQuickCandidacyByContactId = async () => {
  const currentRequestId = ++quickLookupRequestId
  const rawContactObjId = planningStore.getCandidatContactObjId()

  const candidatContactObjId = Number.parseInt(rawContactObjId, 10)
  quickCandidacy.value = null

  if (!Number.isInteger(candidatContactObjId) || candidatContactObjId <= 0) {
    quickCandidacyLoading.value = false
    return
  }

  quickCandidacyLoading.value = true
  try {
    const ofIdsToSearch = (props.planning.ofsIdsEffective && props.planning.ofsIdsEffective.length !== 0)
        ? props.planning.ofsIdsEffective
        : [route.params.ofObjId]

    const results = await Promise.allSettled(
        ofIdsToSearch.map((id) => {
          return planningStore.searchCandidacies(id, route.params.userAccountObjId, String(candidatContactObjId))
        })
    )

    if (currentRequestId !== quickLookupRequestId) {
      return
    }

    const allCandidates = results.flatMap((result) => {
      if (result.status === 'fulfilled' && Array.isArray(result.value)) {
        return result.value
      }
      return []
    })

    quickCandidacy.value = allCandidates.find((candidate) => {
      return Number(candidate?.candidacyObjId) === candidatContactObjId
    }) || null
  } catch {
    if (currentRequestId !== quickLookupRequestId) {
      return
    }
    quickCandidacy.value = null
  } finally {
    if (currentRequestId === quickLookupRequestId) {
      quickCandidacyLoading.value = false
    }
  }
}

const {
  searchTerm,
  suggestions,
  searchLoading,
  onSearch,
  clearSuggestions,
  clearSearch,
  selectSuggestion
} = useSearchAutocomplete(searchFunction, SEARCH_CONFIG.DEBOUNCE_MS, SEARCH_CONFIG.MIN_LENGTH)

const selectQuickCandidacy = () => {
  if (quickCandidacy.value) {
    selectCandidacy(quickCandidacy.value)
  }
}

watch(() => props.meeting, (newMeeting) => {
  formData.candidacyObjId = newMeeting.candidacyObjId
  formData.meetingStatut = Number(newMeeting.statut)
  originalMeetingStatut.value = Number(newMeeting.statut)
  formData.comments = typeof newMeeting.comments === 'string' ? newMeeting.comments : ''
  formData.public = Boolean(newMeeting.public)
  formData.hasMeetLink = newMeeting.meetLink !== null && newMeeting.meetLink !== undefined
  formData.meetLink = typeof newMeeting.meetLink === 'string' ? newMeeting.meetLink : ''

  // si meeting a déjà une candidature jointe, pré-remplir le champ visible
  if (newMeeting.candidacy && (newMeeting.candidacy.firstname || newMeeting.candidacy.lastname)) {
    searchTerm.value = formatPersonDisplayName(newMeeting.candidacy)
    selectedCandidacy.value = newMeeting.candidacy
  } else {
    searchTerm.value = ''
    selectedCandidacy.value = null
  }

  loadQuickCandidacyByContactId()
}, {immediate: true})

const setMeetingStatut = (statut) => {
  if (
    formData.public &&
    formData.meetingStatut === PLANNING_STATUS.PROPOSED &&
    statut === PLANNING_STATUS.ACCEPTED
  ) {
    const confirmed = window.confirm(
      "Ce créneau a une visibilité publique, il est conseillé d'envoyer la convocation pour permettre au candidat de confirmer son choix et d'avoir tous les éléments concernant son entretien, souhaitez-vous tout de même changer le statut de l'entretien en accepté ?"
    )
    if (!confirmed) return
  }
  formData.meetingStatut = formData.meetingStatut === statut ? originalMeetingStatut.value : statut
}

watch(() => formData.hasMeetLink, (hasMeetLink) => {
  if (!hasMeetLink) {
    formData.meetLink = ''
  }
})



const close = () => {
  emit('close');
}

const cancelMeeting = async () => {
  if (window.confirm('Ce créneau ne sera plus disponible pour un entretien, si la convocation a été envoyé, alors une annulation sera envoyé au candidat')) {
    formData.meetingStatut = PLANNING_STATUS.CANCELLED
    await submitForm()
  }
}


const submitForm = async () => {
  loading.value = true
  error.value = null

  if (!isMeetLinkValid.value) {
    loading.value = false
    error.value = 'Le lien visio doit être une URL HTTPS valide'
    return
  }

  // Préparer les données pour l'API
  const updateData = {}


  updateData.candidacyObjId = formData.candidacyObjId
  updateData.meetingStatut = formData.meetingStatut
  updateData.comments = formData.comments
  updateData.public = Boolean(formData.public)
  updateData.meetLink = formData.hasMeetLink ? normalizedMeetLink.value : ''


  // Si aucun changement, ne pas envoyer la requête
  if (Object.keys(updateData).length === 0) {
    close()
    return
  }

  planningStore.updateMeeting(
      route.params.ofObjId,
      route.params.userAccountObjId,
      props.meeting.id,
      updateData
  ).then(() => {
    emit('updated')
    close()
  }).catch((err) => {
    error.value = err.message || err
  }).finally(() => {
    loading.value = false
  })
}

const selectCandidacy = (candidacy) => {
  const selected = selectSuggestion(candidacy)
  formData.candidacyObjId = selected.candidacyObjId
  selectedCandidacy.value = selected
}

const clearSelection = () => {
  clearSearch()
  formData.candidacyObjId = null
  selectedCandidacy.value = null
}
</script>

<style scoped>
.planning .modal-content {
  max-width: 700px;
}

.planning .modal-content--with-map {
  max-width: 1200px;
}

.meeting-summary {
  background: #f8fafc;
  border: 1px solid #e3e7ef;
  border-radius: 10px;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
}

.meeting-summary-head {
  margin-bottom: 0.5rem;
}

.meeting-summary-kicker {
  color: #5f6773;
  font-size: 0.78rem;
  margin: 0;
  text-transform: uppercase;
}

.meeting-summary-date {
  color: #1f2937;
  display: block;
  font-size: 1rem;
}

.meeting-summary-time {
  color: #3f4d63;
  font-size: 0.9rem;
  margin: 0.1rem 0 0.4rem;
}

.meeting-summary-address-wrap {
  border-top: 1px dashed #d8dee8;
  margin-top: 0.55rem;
  padding-top: 0.55rem;
}

.meeting-summary-address-title {
  color: #333;
  display: block;
  font-size: 0.78rem;
  margin-bottom: 0.1rem;
}

.slot-address {
  font-style: normal;
  margin: 0;
}

.slot-address-line {
  align-items: baseline;
  color: #4b5563;
  display: flex;
  font-size: 0.83rem;
  gap: 8px;
}

.slot-address-pin {
  color: #e63946;
  flex-shrink: 0;
  font-size: 1.1em;
}

.slot-address-city {
  color: #666;
}

.slot-meet-link {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.82rem;
  margin: 0 0 4px 0;
}

.slot-meet-link-label {
  color: #333;
  font-size: 0.78rem;
  margin: 0;
  white-space: nowrap;
}

.slot-meet-link-anchor {
  align-items: center;
  color: #0e6076;
  display: inline-flex;
  font-size: 0.82rem;
  gap: 0.4rem;
  text-decoration: none;
  word-break: break-all;
}

.slot-meet-link-anchor:hover {
  text-decoration: underline;
}

.slot-meet-link-anchor--placeholder {
  color: #6b7280;
  text-decoration: none;
}

.slot-meet-link-icon {
  font-size: 0.95rem;
  line-height: 1;
}

.planning .form-group label.visibility-switch {
  align-items: center;
  cursor: pointer;
  display: inline-flex !important;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 0 !important;
  min-height: 24px;
  user-select: none;
  vertical-align: middle;
}

.visibility-switch input {
  height: 0;
  opacity: 0;
  position: absolute;
  width: 0;
}

.visibility-slider {
  background-color: #c9ced6;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
  height: 24px;
  position: relative;
  transition: background-color 0.2s ease;
  width: 42px;
}

.visibility-slider::before {
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  content: '';
  height: 18px;
  left: 3px;
  position: absolute;
  top: 3px;
  transition: transform 0.2s ease;
  width: 18px;
}

.visibility-switch input:checked + .visibility-slider {
  background-color: var(--primary);
}

.visibility-switch input:checked + .visibility-slider::before {
  transform: translateX(18px);
}

.visibility-switch input:disabled + .visibility-slider {
  opacity: 0.6;
}

.visibility-switch input:disabled ~ .visibility-label {
  opacity: 0.6;
}

.visibility-label {
  align-items: center;
  color: #4a4f55;
  display: inline-flex;
  font-size: 0.82rem;
  height: 24px;
  line-height: 24px;
  margin: 0;
  white-space: nowrap;
}

.quick-candidacy-wrap {
  margin-top: 0.5rem;
}

.quick-candidacy-title {
  color: #5f6773;
  font-size: 0.78rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.quick-candidacy-loading {
  color: #6b7280;
  font-size: 0.8rem;
  margin: 0;
}

.quick-candidacy-pill {
  align-items: baseline;
  background: linear-gradient(135deg, #f2f8ff 0%, #edf6ff 100%);
  border: 1px solid #cbe2ff;
  border-radius: 999px;
  color: #1e3a5f;
  cursor: pointer;
  display: inline-flex;
  gap: 0.45rem;
  max-width: 100%;
  padding: 0.1rem 0.7rem;
  transition: all 0.15s ease;
}

.quick-candidacy-pill:hover {
  border-color: #7db5ff;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.15);
  transform: translateY(-1px);
}

.quick-candidacy-pill.is-selected {
  background: linear-gradient(135deg, #dff1ff 0%, #d7edff 100%);
  border-color: #4b9dff;
  display: none;
}

.quick-candidacy-pill-name {
  font-weight: 500;
}



.form-label-row {
  align-items: center;
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.35rem;
}

.form-label-row .form-label {
  margin-bottom: 0;
}

.candidacy-distance {
  background: #eef6ff;
  border: 1px solid #c3deff;
  border-radius: 999px;
  color: #1a56a0;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.1rem 0.55rem;
  white-space: nowrap;
}

.quick-candidacy-pill-email {
  color: #5f6f82;
  font-size: 0.75rem;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.modal-map-pane {
  flex: 0 0 70%;
  border-left: 1px solid #e3e7ef;
  background: #f0f2f5;
  position: relative;
  animation: mapSlideIn 0.4s ease forwards;
}

@keyframes mapSlideIn {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.modal-map-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.status-picker {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.status-picker-current {
  align-items: center;
  display: flex;
  gap: 0.5rem;
}

.status-picker-current-label {
  color: #5f6773;
  font-size: 0.82rem;
}

.status-picker-current-value {
  color: #1f2937;
  font-size: 0.82rem;
  font-weight: 600;
}

.status-picker-btns {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.status-picker-btn {
  align-items: center;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  flex: 1;
  font-size: 0.85rem;
  font-weight: 500;
  gap: 0.4rem;
  justify-content: center;
  min-width: 0;
  padding: 0.4rem 0.5rem;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.status-radio {
  border: 2px solid currentColor;
  border-radius: 50%;
  flex-shrink: 0;
  height: 13px;
  position: relative;
  transition: border-color 0.15s ease;
  width: 13px;
}

.status-radio::after {
  border-bottom: 2px solid currentColor;
  border-right: 2px solid currentColor;
  content: '';
  height: 7px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -65%) rotate(45deg) scale(0);
  transition: transform 0.15s ease;
  width: 3px;
}

.is-active .status-radio::after {
  transform: translate(-50%, -65%) rotate(45deg) scale(1);
}

.status-picker-btn--accepted {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #15803d;
}

.status-picker-btn--accepted:hover {
  background: #dcfce7;
  border-color: #86efac;
}

.status-picker-btn--accepted.is-active {
  background: #16a34a;
  border-color: #16a34a;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.3);
  color: #fff;
}

.status-picker-btn--refused {
  background: #fff7f7;
  border-color: #fecaca;
  color: #b91c1c;
}

.status-picker-btn--refused:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.status-picker-btn--refused.is-active {
  background: #dc2626;
  border-color: #dc2626;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
  color: #fff;
}

.status-picker-btn--cancelled {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #4b5563;
}

.status-picker-btn--cancelled:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.status-picker-btn--cancelled.is-active {
  background: #4b5563;
  border-color: #4b5563;
  box-shadow: 0 2px 6px rgba(75, 85, 99, 0.3);
  color: #fff;
}

.form-actions {
  display: grid !important;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  justify-content: end;
  gap: 15px;
  margin-top: 30px;
}

.form-actions .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
</style>
